import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Sun, Bed, Briefcase, Sofa, Users, Film, Home } from 'lucide-react';

const RoomsSection = () => {
  const { t } = useLanguage();

  const groundFloorRooms = [
    { 
      icon: Sun, 
      title: t('ground_floor_reception'), 
      desc: t('ground_floor_reception_desc'),
      size: "~100 m²"
    },
    { 
      icon: Bed, 
      title: t('ground_floor_master'), 
      desc: t('ground_floor_master_desc'),
      size: "32 m²"
    },
    { 
      icon: Briefcase, 
      title: t('ground_floor_office'), 
      desc: t('ground_floor_office_desc'),
      size: "16 m²"
    },
  ];

  const gardenLevelRooms = [
    { 
      icon: Sofa, 
      title: t('garden_level_lounge'), 
      desc: t('garden_level_lounge_desc'),
      size: "33 m²"
    },
    { 
      icon: Users, 
      title: t('garden_level_bedrooms'), 
      desc: t('garden_level_bedrooms_desc'),
      size: "17 + 19 m²"
    },
    { 
      icon: Film, 
      title: t('garden_level_cinema'), 
      desc: t('garden_level_cinema_desc'),
      size: "30 m²"
    },
    { 
      icon: Home, 
      title: t('garden_level_studio'), 
      desc: t('garden_level_studio_desc'),
      size: "30 m²"
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  return (
    <section 
      className="py-24 md:py-32 bg-[#F7F7F5]"
      data-testid="rooms-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Ground Floor */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="h-px bg-[#C5A059] flex-grow max-w-[60px]" />
            <h2 className="text-3xl md:text-4xl text-[#1A2F23] font-['Playfair_Display']">
              {t('ground_floor_title')}
            </h2>
          </div>
          
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {groundFloorRooms.map((room, index) => {
              const Icon = room.icon;
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white p-8 group hover:shadow-lg transition-shadow duration-300"
                  data-testid={`ground-room-${index}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <Icon className="w-8 h-8 text-[#C5A059]" strokeWidth={1} />
                    <span className="text-sm text-[#5C5C59] font-medium">{room.size}</span>
                  </div>
                  <h3 className="text-xl font-medium text-[#1A1A1A] mb-3 font-['Manrope']">
                    {room.title}
                  </h3>
                  <p className="text-sm text-[#5C5C59] leading-relaxed">
                    {room.desc}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </motion.div>

        {/* Garden Level */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center gap-4 mb-3">
            <div className="h-px bg-[#C5A059] flex-grow max-w-[60px]" />
            <h2 className="text-3xl md:text-4xl text-[#1A2F23] font-['Playfair_Display']">
              {t('garden_level_title')}
            </h2>
          </div>
          <p className="text-[#5C5C59] mb-8 ml-[76px]">{t('garden_level_subtitle')}</p>
          
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {gardenLevelRooms.map((room, index) => {
              const Icon = room.icon;
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white p-6 group hover:shadow-lg transition-shadow duration-300"
                  data-testid={`garden-room-${index}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <Icon className="w-6 h-6 text-[#C5A059]" strokeWidth={1} />
                    <span className="text-xs text-[#5C5C59] font-medium">{room.size}</span>
                  </div>
                  <h3 className="text-lg font-medium text-[#1A1A1A] mb-2 font-['Manrope']">
                    {room.title}
                  </h3>
                  <p className="text-sm text-[#5C5C59] leading-relaxed">
                    {room.desc}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default RoomsSection;
